import { useState } from "react";
import { Copy01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Button,
  ButtonSize,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import { handleCopy } from "utils/handleCopy";
import type { Lockbox, GeneratedLockboxCode } from "api/landlord/lockboxes";
import { VENDOR_LABELS } from "views/lockboxes/constants";
import { GenerateLockboxCodeModal } from "./GenerateLockboxCodeModal";

const validityCaption = (
  result: GeneratedLockboxCode,
  timezone?: string | null
) => {
  // Codebox and Igloohome are both non-cancelable, but only Codebox's window is "rest of
  // today" — Igloohome's is a fixed 24h from generation, so it needs the real timestamp too.
  if (result.vendor === "codebox") return "Valid for the rest of today";

  const until = new Date(result.valid_until).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    ...(timezone ? { timeZone: timezone, timeZoneName: "short" } : {})
  });
  return `Valid until ${until}`;
};

export const LockboxCodeGenerator = ({
  lockbox,
  timezone,
  assignAccountHint,
  actionLabels = "full"
}: {
  lockbox: Lockbox;
  timezone?: string | null;
  assignAccountHint: string;
  actionLabels?: "full" | "icon-copy";
}) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const [generatedCode, setGeneratedCode] =
    useState<GeneratedLockboxCode | null>(lockbox.active_on_demand_code);

  const vendorLabel = lockbox.vendor ? VENDOR_LABELS[lockbox.vendor] : "vendor";
  const missingVendorAccount = !lockbox.vendor_account;

  const caption = generatedCode
    ? validityCaption(generatedCode, timezone)
    : null;

  const copyCode = () => generatedCode && handleCopy(generatedCode.code);

  const actions = (
    <div className="flex shrink-0 gap-2">
      <Button size={ButtonSize.SM} onClick={() => setIsConfirming(true)}>
        Re-generate code
      </Button>
      {actionLabels === "icon-copy" ? (
        <IconButton
          size={IconButtonSize.SM}
          variant={IconButtonVariant.BASE}
          HugeIcon={Copy01Icon}
          aria-label="Copy code"
          tooltip="Copy code"
          onClick={copyCode}
        />
      ) : (
        <Button size={ButtonSize.SM} HugeIcon={Copy01Icon} onClick={copyCode}>
          Copy code
        </Button>
      )}
    </div>
  );

  return (
    <>
      <div className="flex items-center justify-between gap-2.5 rounded-lg border border-dashed border-alpha-100 bg-background-900 p-6">
        <Show if={!generatedCode}>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-content-1000">- - - -</p>
            <p className="text-sm font-medium text-content-400">
              The code will appear here
            </p>
          </div>
          <Button
            size={ButtonSize.SM}
            className="shrink-0"
            disabled={missingVendorAccount}
            onClick={() => setIsConfirming(true)}
          >
            Generate lockbox code
          </Button>
        </Show>

        <Show if={Boolean(generatedCode)}>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-content-1000">
              {generatedCode?.code}
            </p>
            <p className="text-sm font-medium text-content-400">{caption}</p>
          </div>
          {actions}
        </Show>
      </div>

      <Show if={missingVendorAccount}>
        <div className="rounded-lg bg-warning-50 p-4">
          <p className="text-sm text-warning-700 font-medium">
            Assign a {vendorLabel} account to this lockbox {assignAccountHint}{" "}
            to generate a code.
          </p>
        </div>
      </Show>

      <GenerateLockboxCodeModal
        opened={isConfirming}
        onClose={() => setIsConfirming(false)}
        onGenerated={(result) => {
          setGeneratedCode(result);
          setIsConfirming(false);
        }}
        lockbox={lockbox}
      />
    </>
  );
};
